import React, { useState } from "react";
import { createReservation } from "../api";
import { errMsg, fmtMoney, fmtDate } from "../utils";

const DOC_TYPES = ["CC", "TI", "CE", "PP"];
const MAX_PAX = 10;

const emptyPax = () => ({
  doc_type: "CC",
  doc_number: "",
  first_name: "",
  last_name: "",
  phone: "",
});

/**
 * Formulario de pasajeros (1..10) para crear el cupón.
 * draft = {date, originId, destId, originName, destName, fare}
 * El cupón es por TRAMO + FECHA (no por horario): no se envía operación ni
 * salida específica; la taquilla asigna la silla y el horario al redimir.
 */
export default function CrearCupon({ draft, onCancel, onCreated }) {
  const [passengers, setPassengers] = useState([emptyPax()]);
  const [contactEmail, setContactEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fare = Number(draft?.fare || 0);
  const total = fare * passengers.length;

  const setField = (i, field, value) =>
    setPassengers((list) =>
      list.map((p, j) => (j === i ? { ...p, [field]: value } : p))
    );

  const addPax = () =>
    setPassengers((list) => (list.length >= MAX_PAX ? list : [...list, emptyPax()]));

  const removePax = (i) =>
    setPassengers((list) => (list.length <= 1 ? list : list.filter((_, j) => j !== i)));

  function validate() {
    for (let i = 0; i < passengers.length; i++) {
      const p = passengers[i];
      if (!p.doc_number.trim()) return `Pasajero ${i + 1}: falta el número de documento.`;
      if (!p.first_name.trim()) return `Pasajero ${i + 1}: faltan los nombres.`;
      if (!p.last_name.trim()) return `Pasajero ${i + 1}: faltan los apellidos.`;
    }
    const docs = passengers.map((p) => p.doc_number.trim().toUpperCase());
    if (new Set(docs).size !== docs.length) {
      return "Hay documentos repetidos: cada pasajero debe tener un documento distinto.";
    }
    const mail = contactEmail.trim();
    if (mail && !/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(mail)) {
      return "El correo no es válido. Ejemplo: nombre@empresa.com";
    }
    return "";
  }

  async function submit() {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        travel_date: draft.date,
        origin_id: Number(draft.originId),
        destination_id: Number(draft.destId),
        // Cupón por tramo+fecha: sin horario ni operación específicos.
        suggested_operation_id: null,
        suggested_departure_time: null,
        contact_email: contactEmail.trim() || null,
        passengers: passengers.map((p) => ({
          doc_type: p.doc_type,
          doc_number: p.doc_number.trim(),
          first_name: p.first_name.trim(),
          last_name: p.last_name.trim(),
          phone: p.phone.trim() || null,
        })),
      };
      const reservation = await createReservation(payload);
      onCreated(reservation);
    } catch (e) {
      // 422 (validación/reglas) y 409 (conflicto): mostrar el mensaje del backend.
      setError(errMsg(e, "No se pudo crear el cupón."));
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="trip-head">
        <div className="route">{draft.originName} → {draft.destName}</div>
        <div className="meta">Fecha del viaje: {fmtDate(draft.date)}</div>
        <div className="meta">Tarifa por pasajero: <b>{fmtMoney(fare)}</b></div>
      </div>

      <div className="banner warn strong">
        El cupón es para este tramo y esta fecha, para cualquier horario disponible.
        NO garantiza silla ni horario: la taquilla asigna la silla al redimir, sujeto
        a disponibilidad.
      </div>

      <div className="section">
        Pasajeros
        <span className="count">{passengers.length} / {MAX_PAX}</span>
      </div>

      {passengers.map((p, i) => (
        <div className="card pax-card" key={i}>
          <div className="pax-head">
            <span className="pax-n">Pasajero {i + 1}</span>
            {passengers.length > 1 && (
              <button type="button" className="pax-del" onClick={() => removePax(i)}>
                Quitar
              </button>
            )}
          </div>

          <div className="row2 doc">
            <div>
              <label>Tipo</label>
              <select
                value={p.doc_type}
                onChange={(e) => setField(i, "doc_type", e.target.value)}
              >
                {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label>Nº documento</label>
              <input
                type="text"
                inputMode="numeric"
                value={p.doc_number}
                onChange={(e) => setField(i, "doc_number", e.target.value)}
                placeholder="Documento"
              />
            </div>
          </div>

          <div className="row2">
            <div>
              <label>Nombres</label>
              <input
                type="text"
                value={p.first_name}
                onChange={(e) => setField(i, "first_name", e.target.value)}
                placeholder="Nombres"
              />
            </div>
            <div>
              <label>Apellidos</label>
              <input
                type="text"
                value={p.last_name}
                onChange={(e) => setField(i, "last_name", e.target.value)}
                placeholder="Apellidos"
              />
            </div>
          </div>

          <label>Teléfono (opcional)</label>
          <input
            type="tel"
            inputMode="tel"
            value={p.phone}
            onChange={(e) => setField(i, "phone", e.target.value)}
            placeholder="Celular del pasajero"
          />
        </div>
      ))}

      {passengers.length < MAX_PAX && (
        <button type="button" className="btn ghost" onClick={addPax}>
          + Agregar pasajero
        </button>
      )}

      <div className="section">Enviar el voucher</div>
      <div className="card">
        <label style={{ marginTop: 0 }}>Correo para enviar el cupón (opcional)</label>
        <input
          type="email"
          inputMode="email"
          autoCapitalize="none"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          placeholder="nombre@correo.com"
        />
        <div className="tot-note" style={{ marginTop: 8 }}>
          Si escribes un correo, le llega el voucher con el código y el QR apenas
          se cree el cupón. Si lo dejas vacío, el cupón se crea igual y puedes
          imprimirlo o enviarlo por WhatsApp.
        </div>
      </div>

      <div className="card total-card">
        <div className="tot-row">
          <span>Tarifa × {passengers.length} pasajero{passengers.length > 1 ? "s" : ""}</span>
          <b>{fmtMoney(total)}</b>
        </div>
        <div className="tot-note">Este valor se aparta de tu cupo mientras el cupón esté activo.</div>
      </div>

      {error && <div className="banner err">{error}</div>}

      <button className="btn accent" onClick={submit} disabled={submitting}>
        {submitting ? "Creando cupón…" : `Confirmar cupón · ${fmtMoney(total)}`}
      </button>
      <button
        className="btn ghost"
        style={{ marginTop: 10 }}
        onClick={onCancel}
        disabled={submitting}
      >
        Cancelar
      </button>
    </>
  );
}
